import { supabase, supabaseAdmin } from '../lib/supabaseClient';

type PaymentSettingsRow = {
  id: string;
  online_payments_enabled: boolean | null;
  updated_at: string | null;
};

export const PAYMENT_SETTINGS_PLACEHOLDER_ID = '00000000-0000-0000-0000-000000000000';

export interface PaymentSettingsRecord {
  id: string;
  onlinePaymentsEnabled: boolean;
  updatedAt: string | null;
}

const DEFAULT_PAYMENT_SETTINGS: PaymentSettingsRecord = {
  id: PAYMENT_SETTINGS_PLACEHOLDER_ID,
  onlinePaymentsEnabled: true,
  updatedAt: null,
};

const PAYMENT_SETTINGS_SELECT = `
  id,
  online_payments_enabled,
  updated_at
`;

const mapPaymentSettings = (row?: PaymentSettingsRow | null): PaymentSettingsRecord => {
  if (!row) {
    return DEFAULT_PAYMENT_SETTINGS;
  }

  return {
    id: row.id,
    onlinePaymentsEnabled:
      typeof row.online_payments_enabled === 'boolean'
        ? row.online_payments_enabled
        : DEFAULT_PAYMENT_SETTINGS.onlinePaymentsEnabled,
    updatedAt: row.updated_at ?? null,
  };
};

export async function fetchPaymentSettings(): Promise<PaymentSettingsRecord> {
  const client = supabaseAdmin ?? supabase;

  const { data, error } = await client
    .from('payment_settings')
    .select(PAYMENT_SETTINGS_SELECT)
    .order('updated_at', { ascending: false })
    .limit(1)
    .maybeSingle();

  if (error) {
    throw error;
  }

  return mapPaymentSettings(data as PaymentSettingsRow | null);
}

export interface SavePaymentSettingsInput {
  id?: string;
  onlinePaymentsEnabled: boolean;
}

export async function savePaymentSettings(
  input: SavePaymentSettingsInput,
): Promise<PaymentSettingsRecord> {
  const client = supabaseAdmin ?? supabase;

  const payload = {
    online_payments_enabled: input.onlinePaymentsEnabled,
    updated_at: new Date().toISOString(),
  };

  const upsertPayload = input.id ? { ...payload, id: input.id } : payload;

  const { data, error } = await client
    .from('payment_settings')
    .upsert(upsertPayload, { onConflict: 'id' })
    .select(PAYMENT_SETTINGS_SELECT)
    .single();

  if (error) {
    throw error;
  }

  return mapPaymentSettings(data as PaymentSettingsRow);
}
